import "./EditDatesPanel.css";

import { Dispatch, SetStateAction } from "react";

import { ProjectDatesConstants } from "../../constants";
import { ProjectPhase } from "../../constants/project-phase";
import { ProjectDatesData, ProjectPhaseStatus } from "../../models";
import { NextPreviousPhaseButtons } from "../../route/shared/workflows/workflow-templates/tasks-board/components";
import {
  Button,
  Column,
  CursorDataGrid,
  Divider,
  ErrorBox,
  FlyoutPanel,
  PhaseSelector,
  Row,
  Toggle,
} from "../../widget";
import { DesignSection, FeasibilitySection, ImplementationSection, OngoingMonitoringSection } from "./components";
import { useEditDatesPanel } from "./useEditDatesPanel";

interface EditDatesPanelProps {
  data: ProjectDatesData;
  setData: Dispatch<SetStateAction<ProjectDatesData | undefined>>;
  show: boolean;
  onClose: () => void;
  canShowCompletePhaseToggle?: boolean;
  shouldRefreshProjectDetails?: () => void;
}

export const EditDatesPanel = ({
  data,
  setData,
  show,
  onClose,
  canShowCompletePhaseToggle = false,
  shouldRefreshProjectDetails,
}: EditDatesPanelProps): JSX.Element => {
  const {
    dateSchema,
    dates,
    errors,
    hasDateError,
    isHandleSubmitLoading,
    phase,
    addedCycleCount,
    columns,
    isLoadingMilestones,
    defaultSortingCriteria,
    hasMilestones,
    refreshMilestones,
    buttonType,
    onChange,
    handlePhaseChange,
    handleToggleCompletePhase,
    handleClosePanel,
    handleSubmit,
    handleUpdateProjectDates,
    handleAddCycle,
    handleRemoveCycle,
    handleNavigateToWorkflowTemplates,
    isCurrentPhaseComplete,
    shouldShowCompletePhaseToggle,
  } = useEditDatesPanel({ data, show, setData, onClose, shouldRefreshProjectDetails });
  const content = (): JSX.Element => {
    return (
      <div className="EditDatesPanelContent">
        {errors && errors.length > 0 && (
          <Row spacingV="ml" justify="between">
            <Column span={12}>
              <ErrorBox text={errors.map((error) => error.message)} />
            </Column>
          </Row>
        )}
        <Row spacingV="l">
          <Column span={12}>
            <PhaseSelector projectPhase={phase} handlePhaseChange={handlePhaseChange} hasHeader={false} />
          </Column>
        </Row>
        {phase === ProjectPhase.feasibility && (
          <FeasibilitySection
            dates={dates}
            handleUpdateProjectDates={handleUpdateProjectDates}
            hasDateError={hasDateError}
            schema={dateSchema}
          />
        )}
        {phase === ProjectPhase.design && (
          <DesignSection
            dates={dates}
            handleUpdateProjectDates={handleUpdateProjectDates}
            hasDateError={hasDateError}
            schema={dateSchema}
          />
        )}
        {phase === ProjectPhase.implementation && (
          <ImplementationSection
            dates={dates}
            handleUpdateProjectDates={handleUpdateProjectDates}
            hasDateError={hasDateError}
            schema={dateSchema}
          />
        )}
        {phase === ProjectPhase.ongoingMonitoring && (
          <OngoingMonitoringSection
            addedCycleCount={addedCycleCount}
            subsequentFrequencySelectData={ProjectDatesConstants.ONGOING_MONITORING_SUBSEQUENT_FREQUENCY_SELECT_DATA}
            dates={dates}
            handleUpdateProjectDates={handleUpdateProjectDates}
            hasDateError={hasDateError}
            handleAddCycle={handleAddCycle}
            handleRemoveCycle={handleRemoveCycle}
            schema={dateSchema}
          />
        )}
        {canShowCompletePhaseToggle && shouldShowCompletePhaseToggle && (
          <Row spacingV="xl">
            <Column span={12}>
              <div className="EditDatesPanelPhaseCompleteToggleContainer">
                <Toggle
                  value={isCurrentPhaseComplete}
                  onChange={handleToggleCompletePhase}
                  variant="primary"
                  size="medium"
                  label="Mark phase as complete"
                />
              </div>
            </Column>
          </Row>
        )}

        {((phase === ProjectPhase.feasibility && dates.feasibility.status !== ProjectPhaseStatus.COMPLETED) ||
          (phase === ProjectPhase.design && dates.design.status !== ProjectPhaseStatus.COMPLETED) ||
          (phase === ProjectPhase.implementation && dates.implementation.status !== ProjectPhaseStatus.COMPLETED) ||
          (phase === ProjectPhase.ongoingMonitoring &&
            dates.ongoingMonitoring.overallStatus !== ProjectPhaseStatus.COMPLETED)) && (
          <>
            <Row spacingV="xl">
              <Column span={12}>
                <Divider type="horizontal" />
              </Column>
            </Row>

            {hasMilestones ? (
              <>
                <Row spacingV="m">
                  <Column span={12}>
                    <p className="body01">Milestone</p>
                  </Column>
                </Row>
                <Row spacingV="xl">
                  <Column span={12}>
                    <p className="body2">
                      When you change a phase’s start or end date, make sure your milestones stay within the new range
                      so they display correctly. If non-milestone tasks fall outside the range, please adjust them on
                      the task board.
                    </p>
                  </Column>
                </Row>
              </>
            ) : (
              <div className="NoMilestonesYetContainer">
                <Row spacingV="xl" align="center">
                  <Column span={8}>
                    <Row spacingV="m">
                      <Column span={12}>
                        <h1>No milestones yet</h1>
                      </Column>
                    </Row>
                    <Row>
                      <Column span={12}>
                        <p className="body2">
                          To set milestones, apply an existing workflow or create your own for this project
                        </p>
                      </Column>
                    </Row>
                  </Column>
                  <Column span={4} justify="end">
                    <Button text="Go to workflows" onClick={handleNavigateToWorkflowTemplates} hasNoPadding />
                  </Column>
                </Row>
                <Row spacingV="xl">
                  <Column span={12}>
                    <Divider type="horizontal" />
                  </Column>
                </Row>
              </div>
            )}
          </>
        )}
        <Row>
          <Column span={12}>
            <CursorDataGrid
              data={[]}
              onChange={onChange}
              dataIsLoading={isLoadingMilestones}
              columns={columns}
              clientSide={false}
              showTotalCount={false}
              filterable
              defaultSortingCriteria={defaultSortingCriteria}
              refresh={refreshMilestones}
            />
          </Column>
        </Row>
        <form className="formButtons" onSubmit={(e) => handleSubmit(e)}>
          <NextPreviousPhaseButtons currentPhase={phase} handlePhaseChange={handlePhaseChange} />
          <Button text="Save" type="submit" variant={buttonType} isLoading={isHandleSubmitLoading} />
        </form>
      </div>
    );
  };

  return (
    <FlyoutPanel
      show={show}
      onClose={handleClosePanel}
      size="Large"
      header="Edit phase dates"
      buttonsChildren={undefined}
      contentChildren={content()}
    />
  );
};
